import React from "react";
import { Helmet } from "react-helmet-async";
import { Truck, Clock, Wallet, CheckCircle, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const ZONES = [
  { label: "Ленинский район", price: "250 ₽" },
  { label: "Засвияжский район", price: "300 ₽" },
  { label: "Железнодорожный район", price: "300 ₽" },
  { label: "Заволжский район", price: "350 ₽" },
  { label: "Область", price: "250 ₽ + 20 ₽/км" },
];

const Delivery = () => {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="delivery-page">
      <Helmet>
        <title>Доставка пирогов Ульяновск — на дом и в офис | Ишеевские пироги</title>
        <meta name="description" content="Доставка домашних пирогов по Ульяновску. Ленинский, Засвияжский, Заволжский, Железнодорожный районы. От 1500 ₽, бесплатно от 3000 ₽. Ежедневно с 9:00 до 21:00." />
        <link rel="canonical" href="https://pirogi73.ru/dostavka-pirogov-ulyanovsk/" />
        <meta property="og:title" content="Доставка пирогов по Ульяновску | Ишеевские пироги" />
        <meta property="og:description" content="Домашние пироги с доставкой во все районы Ульяновска. От 1500 ₽. Бесплатно от 3000 ₽." />
        <meta property="og:url" content="https://pirogi73.ru/dostavka-pirogov-ulyanovsk/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Доставка пирогов по Ульяновску",
          "provider": {
            "@type": "LocalBusiness",
            "name": "Ишеевские пироги",
            "telephone": "+79378764537",
            "url": "https://pirogi73.ru"
          },
          "areaServed": { "@type": "City", "name": "Ульяновск" },
          "description": "Доставка домашних пирогов и пирожков по Ульяновску и Ульяновской области."
        })}</script>
      </Helmet>

      <div className="label-eyebrow mb-4">Доставка по Ульяновску</div>
      <h1 className="font-display text-5xl sm:text-6xl text-[#2C2520] leading-tight mb-6">
        Доставка пирогов
        <br />
        по Ульяновску и области
      </h1>
      <p className="text-lg text-[#5C4F45] max-w-2xl leading-relaxed mb-12">
        Привезём свежую выпечку в любой район города — к вашему времени, в целости и тепле.
        Минимальный заказ от 1500 ₽, бесплатная доставка от 3000 ₽.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
        {[
          { icon: Clock, title: "Ежедневно", text: "Приём заказов с 9:00 до 21:00, без выходных" },
          { icon: Wallet, title: "Бесплатно от 3000 ₽", text: "При заказе от 3000 ₽ доставка по городу бесплатна" },
          { icon: Truck, title: "К точному времени", text: "Уточняем удобное время — привозим вовремя" },
          { icon: CheckCircle, title: "От 1500 ₽", text: "Минимальная сумма заказа для оформления доставки" },
        ].map((b, i) => (
          <div key={i} className="bg-white border border-[#E8E3DA] rounded-[24px] p-7" data-testid={`delivery-feature-${i}`}>
            <div className="w-12 h-12 rounded-full bg-[#F2EBE1] text-[#C65D3B] flex items-center justify-center mb-5">
              <b.icon size={20} />
            </div>
            <h3 className="font-display text-2xl text-[#2C2520] mb-2">{b.title}</h3>
            <p className="text-sm text-[#5C4F45]">{b.text}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E8E3DA] rounded-[28px] overflow-hidden mb-12">
        <div className="px-8 py-7 border-b border-[#E8E3DA]">
          <h2 className="font-display text-3xl text-[#2C2520]">Стоимость доставки по районам</h2>
        </div>
        <ul className="divide-y divide-[#E8E3DA]">
          {ZONES.map((z, i) => (
            <li key={i} className="flex items-center justify-between px-8 py-5" data-testid={`zone-row-${i}`}>
              <div className="flex items-center gap-3">
                <CheckCircle size={18} className="text-[#C65D3B]" />
                <span className="text-[#2C2520] font-medium">{z.label}</span>
              </div>
              <span className="text-[#5C4F45] font-semibold">{z.price}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="p-8 rounded-[24px] bg-[#F2EBE1] border border-[#E8E3DA]">
          <h2 className="font-display text-2xl text-[#2C2520] mb-4">Как сделать заказ</h2>
          <ol className="space-y-3 text-[#5C4F45]">
            {[
              "Выберите пироги в каталоге",
              "Позвоните или оформите заказ онлайн",
              "Укажите адрес и удобное время",
              "Получите свежую выпечку у двери",
            ].map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C65D3B] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="p-8 rounded-[24px] bg-[#2C2520] text-[#F2EBE1]">
          <div className="label-eyebrow text-[#C65D3B] mb-3">Оплата</div>
          <p className="text-[#F2EBE1]/90 leading-relaxed mb-6">
            Оплата наличными или картой при получении. После оформления заявки менеджер перезвонит для подтверждения заказа.
          </p>
          <a href="tel:+79378764537" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C65D3B] hover:bg-[#A54C2F] transition-colors font-medium">
            <Phone size={16} />
            +7 937 876-45-37
          </a>
        </div>
      </div>

      <div className="bg-white border border-[#E8E3DA] rounded-[28px] p-8 mb-12">
        <h2 className="font-display text-3xl text-[#2C2520] mb-6">Доставка пирогов в Ульяновске — подробнее</h2>
        <div className="space-y-4 text-[#5C4F45] leading-relaxed">
          <p>
            Мы доставляем домашние пироги по всему Ульяновску — от центра города до Заволжья и Нового Города. Время доставки согласовывается при оформлении заказа: вы называете удобный промежуток, мы стараемся приехать точно в срок.
          </p>
          <p>
            Заказать пироги с доставкой в Ульяновске можно как к определённому времени — например, к началу праздника или рабочего обеда — так и в течение дня. Для срочных заказов рекомендуем звонить заранее: за 2–3 часа до нужного времени.
          </p>
          <p>
            Все пироги готовятся на заказ из свежих продуктов. Мы не держим готовую выпечку на складе — каждый пирог делается после подтверждения заказа, поэтому он всегда свежий.
          </p>
        </div>
      </div>

      <div className="text-center">
        <Link to="/pirogi-na-zakaz/" className="btn-primary mr-4">Условия заказа →</Link>
        <Link to="/" className="inline-flex items-center px-6 py-3 rounded-full border border-[#E8E3DA] text-[#2C2520] hover:border-[#C65D3B] transition-colors">К каталогу</Link>
      </div>
    </div>
  );
};

export default Delivery;
